/**
 * @file Level lab: the debug window's "Levels" tab (issue: level manager).
 *
 * Mirrors the upstream threejs-procedural-dungeon demo workflow: pick a theme
 * and a seed, regenerate the level in place, and inspect the WHOLE map from an
 * overhead overview while the stats readout says whether the generation is
 * valid. An assets section toggles every catalog prop kind and scales the
 * overall decor density — each change regenerates immediately, so the effect
 * of every knob is visible the moment it is touched.
 *
 * The tab holds no generation state of its own: every knob lives in
 * {@link ../config/LevelLab.js levelLab}, which the arena reads at build time.
 */

import { THEMES } from '../world/dungeon/generateDungeon.js';
import { DUNGEON_PROPS } from '../config/DungeonProps.js';
import { levelLab, LAB_PROP_KEYS, resetLevelLab } from '../config/LevelLab.js';
import { saveDebugPrefsFrom } from '../config/DebugPrefs.js';
import { buildLevel, teardownLevel } from '../world/Level.js';

/** Prop kinds that are placement-owned dressing vs solid/structured pieces. */
const SWEEP_KEYS = LAB_PROP_KEYS.filter((k) => DUNGEON_PROPS[k].placement === 'themeSweep');
const STRUCT_KEYS = LAB_PROP_KEYS.filter((k) => DUNGEON_PROPS[k].placement !== 'themeSweep');

export class LevelLabPanel {
  /**
   * @param {HTMLElement} host Tab body handed out by the debug window.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   */
  constructor(host, ctx) {
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {HTMLElement} @private */
    this._host = host;
    /** @type {boolean} Whether the map overview camera is active. @private */
    this._overview = true;
    /** @type {boolean} @private */
    this._overviewApplied = false;
    /** @type {THREE.Fog | null} Fog swapped out while the overview is up. @private */
    this._savedFog = null;
    this._build();
  }

  /** @returns {void} @private */
  _build() {
    const themeOptions = ['auto', ...Object.keys(THEMES)]
      .map((key) => `<option value="${key}">${key}</option>`)
      .join('');
    const propRow = (key) => `
      <label class="level-lab__prop">
        <input type="checkbox" data-lab-prop="${key}" ${levelLab.propEnabled[key] ? 'checked' : ''} />
        <span>${key}</span>
      </label>`;

    this._host.innerHTML = `
      <div class="level-lab">
        <section class="level-lab__section">
          <h4 class="level-lab__heading">Generate</h4>
          <div class="level-lab__row">
            <label>Theme
              <select data-lab="theme">${themeOptions}</select>
            </label>
            <label>Seed
              <input type="number" data-lab="seed" placeholder="random" />
            </label>
          </div>
          <div class="level-lab__row">
            <button type="button" class="level-lab__btn level-lab__btn--primary" data-lab="generate">Generate</button>
            <button type="button" class="level-lab__btn" data-lab="random">Random seed</button>
            <label class="level-lab__check">
              <input type="checkbox" data-lab="overview" checked />
              <span>Map overview</span>
            </label>
          </div>
        </section>

        <section class="level-lab__section">
          <h4 class="level-lab__heading">Validation</h4>
          <div class="level-lab__stats" data-lab="stats">Generate a level to see its stats.</div>
        </section>

        <section class="level-lab__section">
          <h4 class="level-lab__heading">Assets
            <span class="level-lab__hint">density ×<span data-lab="densityValue">1.0</span></span>
          </h4>
          <input type="range" min="0" max="2" step="0.1" value="1" data-lab="density" class="level-lab__slider" />
          <div class="level-lab__props-title">Structured props</div>
          <div class="level-lab__props">${STRUCT_KEYS.map(propRow).join('')}</div>
          <div class="level-lab__props-title">Atmosphere sweeps</div>
          <div class="level-lab__props">${SWEEP_KEYS.map(propRow).join('')}</div>
          <div class="level-lab__row">
            <button type="button" class="level-lab__btn" data-lab="reset">Reset lab</button>
          </div>
        </section>
      </div>
    `;

    const el = (q) => /** @type {HTMLElement} */ (this._host.querySelector(q));
    this._els = {
      theme: /** @type {HTMLSelectElement} */ (el('[data-lab="theme"]')),
      seed: /** @type {HTMLInputElement} */ (el('[data-lab="seed"]')),
      stats: el('[data-lab="stats"]'),
      density: /** @type {HTMLInputElement} */ (el('[data-lab="density"]')),
      densityValue: el('[data-lab="densityValue"]'),
      overview: /** @type {HTMLInputElement} */ (el('[data-lab="overview"]'))
    };

    // Restore the lab exactly as the last session left it.
    this._els.theme.value = levelLab.theme;
    this._els.seed.value = levelLab.seed != null ? String(levelLab.seed) : '';
    this._els.density.value = String(levelLab.densityMult);
    this._els.densityValue.textContent = levelLab.densityMult.toFixed(1);

    el('[data-lab="generate"]').addEventListener('click', () => this._generate());
    el('[data-lab="random"]').addEventListener('click', () => {
      this._els.seed.value = String((Math.random() * 0xffffffff) >>> 0);
      this._generate();
    });
    this._els.overview.addEventListener('change', () => {
      this._overview = this._els.overview.checked;
      this._applyOverview();
    });
    this._els.theme.addEventListener('change', () => {
      levelLab.theme = this._els.theme.value;
      this._generate();
    });
    this._els.density.addEventListener('input', () => {
      levelLab.densityMult = Number(this._els.density.value);
      this._els.densityValue.textContent = levelLab.densityMult.toFixed(1);
    });
    this._els.density.addEventListener('change', () => this._generate());
    this._host.addEventListener('change', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      const key = target.getAttribute('data-lab-prop');
      if (!key) return;
      levelLab.propEnabled[key] = target.checked;
      this._generate();
    });
    el('[data-lab="reset"]').addEventListener('click', () => {
      resetLevelLab();
      saveDebugPrefsFrom({ lab: levelLab });
      this._els.theme.value = 'auto';
      this._els.seed.value = '';
      this._els.density.value = '1';
      this._els.densityValue.textContent = '1.0';
      for (const box of this._host.querySelectorAll('[data-lab-prop]')) {
        const key = /** @type {HTMLInputElement} */ (box).getAttribute('data-lab-prop');
        /** @type {HTMLInputElement} */ (box).checked = Boolean(key && levelLab.propEnabled[key]);
      }
      this._generate();
    });
  }

  /**
   * Regenerates the level in place with the lab's knobs and refreshes stats.
   * A blank seed rolls a fresh one and writes it back to the input, so every
   * generation stays reproducible.
   * @returns {void}
   * @private
   */
  _generate() {
    const ctx = this._ctx;
    if (!ctx.arena) {
      this._els.stats.textContent = 'Start a run first: the lab regenerates the live level.';
      return;
    }
    if (this._els.seed.value === '') {
      this._els.seed.value = String((Math.random() * 0xffffffff) >>> 0);
    }
    levelLab.seed = Number(this._els.seed.value) >>> 0;
    levelLab.theme = this._els.theme.value;

    teardownLevel(ctx);
    const ok = buildLevel(ctx);
    saveDebugPrefsFrom({ lab: levelLab });
    this._renderStats(ok);
    this._overviewApplied = false;
    this._applyOverview();
  }

  /** @returns {void} @private */
  _applyOverview() {
    const ctx = this._ctx;
    if (this._overview && ctx.arena?.bounds) {
      ctx.cameraController.setOverview(ctx.arena.bounds);
      // The gameplay fog ends ~44 world units out; an overview camera sits far
      // beyond that, so the whole map would drown in it. Inspection wants the
      // level bare — the fog comes back the moment the overview ends.
      if (!this._savedFog && ctx.scene.fog) {
        this._savedFog = ctx.scene.fog;
        ctx.scene.fog = null;
      }
      this._overviewApplied = true;
    } else if (this._overviewApplied) {
      ctx.cameraController.setOverview(null);
      if (this._savedFog) {
        ctx.scene.fog = this._savedFog;
        this._savedFog = null;
      }
      this._overviewApplied = false;
    }
  }

  /**
   * @param {boolean} ok Whether the last build succeeded.
   * @returns {void}
   * @private
   */
  _renderStats(ok) {
    const arena = this._ctx.arena;
    const d = arena?.dungeon;
    if (!ok || !d) {
      this._els.stats.innerHTML = '<b class="level-lab__bad">Generation failed</b> — every attempt was rejected.';
      return;
    }
    const s = d.stats;
    const cell = (label, value) =>
      `<div class="level-lab__stat"><span>${label}</span><b>${value}</b></div>`;
    this._els.stats.innerHTML = `
      <div class="level-lab__name">${arena.name} <em>(${arena.themeKey})</em></div>
      <div class="level-lab__grid">
        ${cell('valid', d.valid ? 'yes' : 'NO')}
        ${cell('attempts', s.attempts)}
        ${cell('gen', `${s.genMs.toFixed(1)} ms`)}
        ${cell('rooms', s.rooms)}
        ${cell('loops', s.loops)}
        ${cell('floor tiles', s.floorTiles)}
        ${cell('enemies', `${s.enemies}/${s.enemyTarget}`)}
        ${cell('solid props', s.solidProps)}
        ${cell('formations', s.formations.join(', ') || '—')}
      </div>
    `;
  }

  /**
   * Turns the overview off when the tab (or the window) goes away, so closing
   * the lab always hands the camera back to the player.
   * @param {boolean} visible Whether this tab is the active, open tab.
   * @returns {void}
   */
  setVisible(visible) {
    if (!visible && this._overviewApplied) {
      this._ctx.cameraController.setOverview(null);
      this._overviewApplied = false;
    } else if (visible && this._overview && this._ctx.arena) {
      this._applyOverview();
    }
  }

  /** @returns {void} */
  dispose() {
    if (this._overviewApplied) {
      this._ctx.cameraController.setOverview(null);
      if (this._savedFog) {
        this._ctx.scene.fog = this._savedFog;
        this._savedFog = null;
      }
    }
    this._host.innerHTML = '';
  }
}
